<template>
  <form @submit.prevent novalidate="true">
    <b-card>
      <template slot="header">
        <div class="form-group form-row mb-0">
          <div class="col-lg-6">
            <treeselect :value="surveyValue"
                        :defaultExpandLevel="Infinity"
                        :clearable="false"
                        :placeholder="$t('Webportal.StructureFilter.Placeholder')"
                        :options="structureItems"
                        :disableFuzzyMatching="true"
                        :disable-branch-nodes="true"
                        :disabled="isBusy"
                        @input="surveyInput">
            </treeselect>
          </div>
          <div class="col-1 ml-auto" v-bind:class="{ inlineSpinner: isBusy }"></div>
        </div>
      </template>

      <div v-if="surveySettings">
        <div class="form-group form-row">
          <label for="ShowCheckReportClusterOverview" class="col-lg-4 col-md-6 col-form-label pt-0">
            {{ $t('Webportal.Administration.ShowCheckReportClusterOverview') }}
            <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportClusterOverview.Description')">
              <font-awesome-icon :icon="['fas', 'info-circle']" />
            </span>
          </label>
          <div class="col">
            <label class="switch switch-label switch-secondary">
              <input class="switch-input" type="checkbox"
                    v-model="surveySettings.ShowCheckReportClusterOverview"
                    v-validate data-vv-name="ShowCheckReportClusterOverview"
                    :disabled="isBusy">
              <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
            </label>
          </div>
        </div>

        <div class="form-group form-row">
          <label for="ShowCheckReportWaveCompare" class="col-lg-4 col-md-6 col-form-label pt-0">
            {{ $t('Webportal.Administration.ShowCheckReportWaveCompare') }}
            <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportWaveCompare.Description')">
              <font-awesome-icon :icon="['fas', 'info-circle']" />
            </span>
          </label>
          <div class="col">
            <label class="switch switch-label switch-secondary">
              <input class="switch-input" type="checkbox"
                    v-model="surveySettings.ShowCheckReportWaveCompare"
                    v-validate data-vv-name="ShowCheckReportWaveCompare"
                    :disabled="isBusy">
              <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
            </label>
          </div>
        </div>

        <div class="form-group form-row">
          <label for="ShowCheckReportKpiOverview" class="col-lg-4 col-md-6 col-form-label pt-0">
            {{ $t('Webportal.Administration.ShowCheckReportKpiOverview') }}
            <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportKpiOverview.Description')">
              <font-awesome-icon :icon="['fas', 'info-circle']" />
            </span>
          </label>
          <div class="col">
            <label class="switch switch-label switch-secondary">
              <input class="switch-input" type="checkbox"
                    v-model="surveySettings.ShowCheckReportKpiOverview"
                    v-validate data-vv-name="ShowCheckReportKpiOverview"
                    :disabled="isBusy">
              <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
            </label>
          </div>
        </div>

        <hr />

        <!-- Scope of previous waves -->
        <div class="form-group form-row">
          <label for="ScopeOfPreviousWavesForCheckReport" class="col-lg-4 col-md-6 col-form-label pt-0">
            {{ $t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport') }}
            <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport.Description')">
              <font-awesome-icon :icon="['fas', 'info-circle']" />
            </span>
          </label>
          <div class="col">
            <b-form-radio-group id="ScopeOfPreviousWavesForCheckReport"
                                buttons button-variant="outline-secondary" size="sm"
                                v-model="surveySettings.ScopeOfPreviousWavesForCheckReport"
                                :disabled="isBusy"
                                v-validate="'required'"
                                data-vv-name="ScopeOfPreviousWavesForCheckReport">
              <b-form-radio :value="0">{{ $t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport.Survey') }}
                <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport.Survey.Description')">
                  <font-awesome-icon :icon="['fas', 'info-circle']" />
                </span>
              </b-form-radio>
              <b-form-radio :value="1">{{ $t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport.Project') }}
                <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport.Project.Description')">
                  <font-awesome-icon :icon="['fas', 'info-circle']" />
                </span>
              </b-form-radio>
            </b-form-radio-group>
          </div>
        </div>

        <div class="form-group form-row">
          <label for="NumberOfPreviousWavesForCheckReport" class="col-lg-4 col-md-6 col-form-label pt-0">
            {{ $t('Webportal.Administration.NumberOfPreviousWavesForCheckReport') }}
            <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.NumberOfPreviousWavesForCheckReport.Description')">
              <font-awesome-icon :icon="['fas', 'info-circle']" />
            </span>
          </label>
          <div class="col">
            <b-form-radio-group id="NumberOfPreviousWavesForCheckReport"
                                buttons button-variant="outline-secondary" size="sm"
                                v-model="surveySettings.NumberOfPreviousWavesForCheckReport"
                                :options="previousWavesOptions"
                                :disabled="isBusy"
                                v-validate="'required'"
                                data-vv-name="NumberOfPreviousWavesForCheckReport" />
          </div>
        </div>

        <hr />

        <div class="form-group form-row">
          <label for="ShowCheckReportClusterScore" class="col-lg-4 col-md-6 col-form-label pt-0">
            {{ $t('Webportal.Administration.ShowCheckReportClusterScore') }}
            <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportClusterScore.Description')">
              <font-awesome-icon :icon="['fas', 'info-circle']" />
            </span>
          </label>
          <div class="col">
            <label class="switch switch-label switch-secondary">
              <input class="switch-input" type="checkbox"
                    v-model="surveySettings.ShowCheckReportClusterScore"
                    v-validate data-vv-name="ShowCheckReportClusterScore"
                    :disabled="isBusy">
              <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
            </label>
          </div>
        </div>

        <div class="form-group form-row">
          <label for="ShowCheckReportClusterPoints" class="col-lg-4 col-md-6 col-form-label pt-0">
            {{ $t('Webportal.Administration.ShowCheckReportClusterPoints') }}
            <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportClusterPoints.Description')">
              <font-awesome-icon :icon="['fas', 'info-circle']" />
            </span>
          </label>
          <div class="col">
            <label class="switch switch-label switch-secondary">
              <input class="switch-input" type="checkbox"
                    v-model="surveySettings.ShowCheckReportClusterPoints"
                    v-validate data-vv-name="ShowCheckReportClusterPoints"
                    :disabled="isBusy">
              <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
            </label>
          </div>
        </div>

        <div class="form-group form-row">
          <label for="ShowCheckReportQuestionPoints" class="col-lg-4 col-md-6 col-form-label pt-0">
            {{ $t('Webportal.Administration.ShowCheckReportQuestionPoints') }}
            <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportQuestionPoints.Description')">
              <font-awesome-icon :icon="['fas', 'info-circle']" />
            </span>
          </label>
          <div class="col">
            <label class="switch switch-label switch-secondary">
              <input class="switch-input" type="checkbox"
                    v-model="surveySettings.ShowCheckReportQuestionPoints"
                    v-validate data-vv-name="ShowCheckReportQuestionPoints"
                    :disabled="isBusy">
              <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
            </label>
          </div>
        </div>

        <hr />

        <div class="form-group form-row">
          <label for="ShowCheckReportThresholdLegend" class="col-lg-4 col-md-6 col-form-label pt-0">
            {{ $t('Webportal.Administration.ShowCheckReportThresholdLegend') }}
            <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportThresholdLegend.Description')">
              <font-awesome-icon :icon="['fas', 'info-circle']" />
            </span>
          </label>
          <div class="col">
            <label class="switch switch-label switch-secondary">
              <input class="switch-input" type="checkbox"
                    v-model="surveySettings.ShowCheckReportThresholdLegend"
                    v-validate data-vv-name="ShowCheckReportThresholdLegend"
                    :disabled="isBusy">
              <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
            </label>
          </div>
        </div>

        <hr />

        <div class="form-group form-row">
          <label class="col-lg-4 col-md-6 col-form-label pt-0">
            {{ $t('Webportal.Administration.InfoPage') }}
            <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.InfoPage.Description')">
              <font-awesome-icon :icon="['fas', 'info-circle']" />
            </span>
          </label>
          <div class="col">
            <image-uploader ref="infoPageUploader"
                            v-model="surveySettings.InfoPageUrl"
                            :upload-url="`api/administration/upload-info-page?survey-id=${surveySettings.SurveyId}`"
                            :previewHeight="150"
                            v-validate="'required'"
                            data-vv-name="InfoPage" />
          </div>
        </div>
      </div>

      <template slot="footer">
        <div class="float-right">
          <button type="button" @click="cancel" class="btn btn-secondary" :disabled="!isFormDirty || isBusy">
            <font-awesome-icon :icon="['far', 'ban']" /> {{ $t('Webportal.Cancel') }}
          </button>
          <button type="button" @click="submit" class="btn btn-primary" :disabled="!isFormDirty || !isFormValid || isBusy">
            <font-awesome-icon :icon="['far', 'dot-circle']" /> {{ $t('Webportal.Save') }}
          </button>
        </div>
      </template>
    </b-card>
  </form>
</template>

<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import SurveysService from '@/services/SurveysService';
import AdministationService from '@/services/AdministationService';
import EventBus from '@/services/EventBus.js';
import ImageUploader from './ImageUploader'

export default {
  name: "SurveySettings",

  mixins: [ AppConfig ],

  components: {
    ImageUploader
  },

  data () {
    return {
      // Filter properties
      structureItems: [],                // Data items for structure treeselect
      surveyValue: null,                 // Selected survey item
      surveySettings: null,

      isBusy: false,

      previousWavesOptions: [
        { text: '1', value: 1 },
        { text: '2', value: 2 },
        { text: '3', value: 3 },
        { text: '4', value: 4 },
        { text: '5', value: 5 }
      ]
    }
  },

  computed: {
    isFormDirty() {
      return Object.keys(this.validationFields).some(key => this.validationFields[key].dirty);
    },
    isFormValid() {
      return Object.keys(this.validationFields).every(key => this.validationFields[key].valid === null || this.validationFields[key].valid);
    }
  },

  activated() {
    this.getSurveyStructure();
    this.getSurveySettings();
    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  deactivated() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  beforeRouteLeave (to, from, next) {
    this.$emit('unsavedChanges', this.isFormDirty, next);
  },

  methods: {
    localizationChanged(localizationId) {
      this.getSurveyStructure();
    },

    // Get survey structure data for the structure treeselect
    getSurveyStructure() {
      SurveysService.getSurveyStructure()
        .then(response => {
          this.structureItems = response.data;

          if (!this.surveyValue)
          {
            // Returns an array of all surveys recursively for the given node
            let returnSurveys = (obj) => {
                return _.concat(_.startsWith(obj.id, 'S') ? obj : [],
                    obj.children ? _.flatMapDeep(obj.children, returnSurveys) : []);
            };
            
            let firstSurvey = _.first(_.flatMapDeep(this.structureItems, returnSurveys));
            this.surveyInput(firstSurvey ? firstSurvey.id : null);
          }
        })
    },

    getSurveySettings() {
      if (this.surveyValue)
      {
        AdministationService.getSurveySettings(this.surveyValue.substring(1))
          .then(response => {
            this.isBusy = false;

            this.surveySettings = response.data ? response.data[0] : null;
            if (this.surveySettings)
            {
              this.surveySettings.InfoPageUrl = 
                this.surveySettings.InfoPageExternalImageId ?
                  process.env.BASE_URL + `api/media/image?id=${this.surveySettings.InfoPageExternalImageId}` :
                  ' ';
            }

            this.$validator.reset();
          });
      }
      else
      {
        this.surveySettings = null;
        this.$validator.reset();
      }
    },

    // Called whenever the input of the structure treeselect changes
    surveyInput(value) {
      if (this.surveyValue !== value)
      {
        let currentValue = this.surveyValue;
        this.surveyValue = value;
        
        this.$emit('unsavedChanges', this.isFormDirty, result => {
          if (result === false)
          {
            this.surveyValue = currentValue;
          }
          else
            this.getSurveySettings();
        });
      }
    },

    cancel() {
      this.$emit('unsavedChanges', this.isFormDirty, result => {
        if (result !== false) this.getSurveySettings();
      });
    },

    submit() {
      this.isBusy = true;

      AdministationService.setSurveySettings(this.surveySettings)
        .then(response => {
          if (this.validationFields['InfoPage'].dirty)
            this.$refs.infoPageUploader.uploadImage();

          this.isBusy = false;
          this.$validator.reset();
        })
        .catch(_ => { this.isBusy = false; } );
    }
  }
}
</script>
